import {
  insideAny,
  isCapitalizedMidSentence,
  phraseRegex,
  quotedSpans,
  type Tally,
  tally,
  withoutOverlaps,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Terms that APA's bias-free language guidance asks writers to reconsider, with the
 * alternatives it offers. Sources: the Publication Manual of the American Psychological
 * Association (7th ed., 2020), chapter 5; APA Style's bias-free language pages and its
 * Brief Guide to Bias-Free and Inclusive Language (2025); and APA's Inclusive Language
 * Guide (2nd ed., 2023).
 *
 * Every rule is a prompt, never a replacement: the right word depends on context and on
 * what the people described call themselves, and identity-first versus person-first
 * language varies by community. So the rules are narrow on purpose —
 *
 * - quoted passages are skipped (a source's words are the source's);
 * - a job title used as a title or a name is skipped ("Chairman Mao", "Freshman Seminar");
 * - "the poor", "the blind" and the like only count when used as a noun for people
 *   ("the poor are…"), not as an adjective ("the poor results", "the blind spot");
 * - "suffers from" only counts when it is about a person or a health condition, not
 *   "the study suffers from a small sample";
 * - field terms stay standard: "the slave trade", "prisoners of war", "Caucasian
 *   languages", "sexual and gender minorities".
 */

export type InclusiveCategory =
  | "gender"
  | "age"
  | "disability"
  | "health"
  | "race"
  | "identity"
  | "ses";

/** Keys into the widget's `notes` strings: the explanation shown with a match. */
export type InclusiveNote =
  | "adjectiveLabel"
  | "caucasian"
  | "chair"
  | "disabilityEuphemism"
  | "enslaved"
  | "eskimo"
  | "firstYear"
  | "genericMan"
  | "hearing"
  | "heOrShe"
  | "homeless"
  | "homosexual"
  | "incarcerated"
  | "intellectualDisability"
  | "layperson"
  | "mentalHealthTerms"
  | "minorities"
  | "nonWhite"
  | "normal"
  | "occupation"
  | "olderAdults"
  | "oppositeSex"
  | "oriental"
  | "seniors"
  | "sexualPreference"
  | "sexWork"
  | "shortStature"
  | "slashPronoun"
  | "substanceUse"
  | "suffers"
  | "suicide"
  | "thirdWorld"
  | "transgender"
  | "victim"
  | "wheelchair";

interface InclusiveRule {
  category: InclusiveCategory;
  note: InclusiveNote;
  phrases: readonly string[];
  /** What to consider instead, best first. Words in the text's language: English. */
  alternatives: readonly string[];
  /** A job title: skip when capitalized mid-sentence or followed by a name ("Chairman Mao"). */
  title?: boolean;
  /** Skip when a capitalized word follows — part of a name ("Oriental Institute"). */
  beforeName?: boolean;
  /** "the poor": only when used as a noun for people. */
  label?: boolean;
  /** Skip when any word of the match is capitalized ("the Deaf community"). */
  capitalizedIsProper?: boolean;
  /** Skip after these words. */
  notAfter?: readonly string[];
  /** Skip before these words or phrases. */
  notBefore?: readonly string[];
  /** Only about a person or a health condition. */
  health?: boolean;
}

const OCCUPATION = (
  phrases: readonly string[],
  alternatives: readonly string[],
  title = false
): InclusiveRule => ({
  category: "gender",
  note: "occupation",
  phrases,
  alternatives,
  title,
});

const RULES: readonly InclusiveRule[] = [
  // ---- Gender
  {
    category: "gender",
    note: "genericMan",
    phrases: ["mankind"],
    alternatives: ["humankind", "humanity", "people"],
  },
  {
    category: "gender",
    note: "genericMan",
    phrases: ["man-made", "manmade"],
    alternatives: ["artificial", "human-made", "synthetic", "manufactured"],
  },
  {
    category: "gender",
    note: "genericMan",
    phrases: ["manpower"],
    alternatives: ["workforce", "personnel", "staff"],
  },
  {
    category: "gender",
    note: "genericMan",
    phrases: ["man-hours", "man hours", "manhours"],
    alternatives: ["person-hours", "work hours", "labor hours"],
  },
  {
    category: "gender",
    note: "genericMan",
    phrases: ["manned", "unmanned"],
    alternatives: ["crewed", "staffed", "uncrewed"],
  },
  {
    category: "gender",
    note: "genericMan",
    phrases: ["man-machine", "man–machine"],
    alternatives: ["human–machine", "user–system"],
  },
  {
    category: "gender",
    note: "genericMan",
    phrases: ["forefathers"],
    alternatives: ["ancestors", "forebears"],
    title: true,
  },
  {
    category: "gender",
    note: "chair",
    phrases: ["chairman", "chairmen", "chairwoman", "chairwomen"],
    alternatives: ["chair", "chairperson"],
    title: true,
  },
  OCCUPATION(
    ["policeman", "policemen", "policewoman", "policewomen"],
    ["police officer"]
  ),
  OCCUPATION(["fireman", "firemen"], ["firefighter"]),
  OCCUPATION(
    ["businessman", "businessmen", "businesswoman", "businesswomen"],
    ["businessperson", "businesspeople", "executive"]
  ),
  OCCUPATION(
    ["salesman", "salesmen", "saleswoman", "saleswomen"],
    ["salesperson", "sales representative"]
  ),
  OCCUPATION(
    ["spokesman", "spokesmen", "spokeswoman", "spokeswomen"],
    ["spokesperson", "representative"]
  ),
  OCCUPATION(
    ["congressman", "congressmen", "congresswoman", "congresswomen"],
    ["member of Congress", "representative", "legislator"],
    true
  ),
  OCCUPATION(
    ["mailman", "mailmen", "postman", "postmen"],
    ["mail carrier", "postal worker"]
  ),
  OCCUPATION(["workman", "workmen"], ["worker"]),
  OCCUPATION(["foreman", "foremen"], ["supervisor"], true),
  OCCUPATION(["cameraman", "cameramen"], ["camera operator"]),
  OCCUPATION(["middleman", "middlemen"], ["intermediary"]),
  OCCUPATION(["stewardess", "stewardesses"], ["flight attendant"]),
  OCCUPATION(["housewife", "housewives"], ["homemaker"]),
  OCCUPATION(
    ["upperclassman", "upperclassmen", "underclassman", "underclassmen"],
    ["upper-level student", "lower-level student"]
  ),
  {
    category: "gender",
    note: "layperson",
    phrases: ["layman", "laymen", "laywoman"],
    alternatives: ["layperson", "nonspecialist", "general reader"],
  },
  {
    category: "gender",
    note: "firstYear",
    phrases: ["freshman", "freshmen"],
    alternatives: ["first-year student"],
    title: true,
  },
  {
    category: "gender",
    note: "heOrShe",
    phrases: [
      "he or she",
      "she or he",
      "his or her",
      "her or his",
      "him or her",
      "her or him",
      "himself or herself",
      "herself or himself",
    ],
    alternatives: ["they", "their", "them", "themselves"],
  },
  {
    category: "gender",
    note: "slashPronoun",
    phrases: [
      "he/she",
      "she/he",
      "s/he",
      "(s)he",
      "his/her",
      "her/his",
      "him/her",
      "her/him",
      "himself/herself",
      "herself/himself",
    ],
    alternatives: ["they", "their", "them", "themselves"],
  },
  {
    category: "gender",
    note: "oppositeSex",
    phrases: ["opposite sex", "opposite gender"],
    alternatives: ["another sex", "a different gender"],
  },

  // ---- Age
  {
    category: "age",
    note: "olderAdults",
    phrases: ["the elderly", "elderly"],
    alternatives: ["older adults", "older people", "adults aged 65 and older"],
  },
  {
    category: "age",
    note: "seniors",
    phrases: ["senior citizen", "senior citizens", "seniors"],
    alternatives: ["older adults", "older people"],
    notAfter: [
      "class",
      "college",
      "graduating",
      "high",
      "school",
      "undergraduate",
      "university",
    ],
    notBefore: [
      "at college",
      "at university",
      "in college",
      "in high school",
      "in university",
    ],
  },
  {
    category: "age",
    note: "olderAdults",
    phrases: ["the aged"],
    alternatives: ["older adults", "older people"],
    label: true,
  },
  {
    category: "age",
    note: "olderAdults",
    phrases: [
      "aging dependents",
      "ageing dependants",
      "aging dependants",
      "ageing dependents",
    ],
    alternatives: ["older adults"],
  },

  // ---- Disability
  {
    category: "disability",
    note: "wheelchair",
    phrases: [
      "wheelchair-bound",
      "wheelchair bound",
      "confined to a wheelchair",
      "confined to wheelchairs",
    ],
    alternatives: ["uses a wheelchair", "wheelchair user"],
  },
  {
    category: "disability",
    note: "disabilityEuphemism",
    phrases: [
      "differently abled",
      "differently-abled",
      "handi-capable",
      "handicapable",
      "handicapped",
      "mentally challenged",
      "multiabled",
      "physically challenged",
      "special needs",
      "special-needs",
    ],
    alternatives: [
      "disabled",
      "person with a disability",
      "disability-related needs",
    ],
  },
  {
    category: "disability",
    note: "intellectualDisability",
    phrases: ["mentally retarded", "mental retardation", "retarded"],
    alternatives: [
      "intellectual disability",
      "person with an intellectual disability",
    ],
    notBefore: [
      "field",
      "fields",
      "potential",
      "potentials",
      "solution",
      "solutions",
      "time",
    ],
  },
  {
    category: "disability",
    note: "hearing",
    phrases: ["hearing-impaired", "hearing impaired"],
    alternatives: ["Deaf", "hard of hearing", "Deaf or hard-of-hearing person"],
  },
  {
    category: "disability",
    note: "adjectiveLabel",
    phrases: ["the disabled", "the handicapped"],
    alternatives: ["disabled people", "people with disabilities"],
    label: true,
  },
  {
    category: "disability",
    note: "adjectiveLabel",
    phrases: ["the blind", "the deaf"],
    alternatives: ["blind people", "Deaf or hard-of-hearing people"],
    label: true,
    capitalizedIsProper: true,
  },
  {
    category: "disability",
    note: "shortStature",
    phrases: ["midget", "midgets"],
    alternatives: [
      "little person",
      "person of short stature",
      "person with dwarfism",
    ],
  },
  {
    category: "disability",
    note: "normal",
    phrases: [
      "abnormal children",
      "abnormal individuals",
      "abnormal people",
      "normal adults",
      "normal child",
      "normal children",
      "normal control group",
      "normal controls",
      "normal individuals",
      "normal participants",
      "normal peers",
      "normal people",
      "normal person",
      "normal persons",
      "normal students",
      "normal subjects",
    ],
    alternatives: [
      "nondisabled",
      "people without disabilities",
      "typically developing",
      "neurotypical",
    ],
  },

  // ---- Health and mental health
  {
    category: "health",
    note: "suffers",
    phrases: [
      "afflicted by",
      "afflicted with",
      "battling",
      "stricken by",
      "stricken with",
      "suffer from",
      "suffer with",
      "suffered from",
      "suffering from",
      "suffering with",
      "suffers from",
      "suffers with",
    ],
    alternatives: ["has", "lives with", "experiences", "was diagnosed with"],
    health: true,
  },
  {
    category: "health",
    note: "victim",
    phrases: [
      "AIDS patient",
      "AIDS patients",
      "AIDS victim",
      "AIDS victims",
      "HIV patient",
      "HIV patients",
      "cancer victim",
      "cancer victims",
      "polio victim",
      "polio victims",
      "stroke victim",
      "stroke victims",
      "victim of a stroke",
      "victims of AIDS",
      "victims of cancer",
      "victims of polio",
      "victims of stroke",
    ],
    alternatives: [
      "person who has had a stroke",
      "person with HIV",
      "person living with cancer",
    ],
  },
  {
    category: "health",
    note: "mentalHealthTerms",
    phrases: [
      "brain damaged",
      "brain-damaged",
      "crazy",
      "insane",
      "lunatic",
      "lunatics",
      "mental defect",
      "mental defects",
      "mentally ill",
      "psycho",
      "psychos",
      "the mentally ill",
    ],
    alternatives: [
      "person with a mental illness",
      "person with a traumatic brain injury",
      "name the behavior",
    ],
  },
  {
    category: "health",
    note: "substanceUse",
    phrases: [
      "addict",
      "addicts",
      "alcohol abuser",
      "alcohol abusers",
      "alcoholics",
      "drug abuser",
      "drug abusers",
      "drug addict",
      "drug addicts",
      "junkie",
      "junkies",
      "substance abuser",
      "substance abusers",
    ],
    alternatives: [
      "person with a substance use disorder",
      "person who uses drugs",
      "person with alcohol use disorder",
    ],
  },
  {
    category: "health",
    note: "substanceUse",
    phrases: ["an alcoholic"],
    alternatives: ["a person with alcohol use disorder"],
    label: true,
  },
  {
    category: "health",
    note: "suicide",
    phrases: [
      "commit suicide",
      "commits suicide",
      "committed suicide",
      "committing suicide",
      "completed suicide",
      "failed suicide",
      "successful suicide",
      "suicided",
      "unsuccessful suicide",
    ],
    alternatives: [
      "died by suicide",
      "death by suicide",
      "suicide attempt",
      "survived a suicide attempt",
    ],
  },

  // ---- Race and ethnicity
  {
    category: "race",
    note: "caucasian",
    phrases: ["Caucasian", "Caucasians"],
    alternatives: ["White", "European American"],
    notBefore: ["language", "languages", "mountains", "region", "republics"],
  },
  {
    category: "race",
    note: "oriental",
    phrases: ["Oriental", "Orientals"],
    alternatives: ["Asian", "Asian American", "name the country or region"],
    notBefore: ["carpet", "carpets", "rug", "rugs"],
    beforeName: true,
  },
  {
    category: "race",
    note: "eskimo",
    phrases: ["Eskimo", "Eskimos"],
    alternatives: ["Inuit", "Yupik", "Iñupiat", "name the specific people"],
  },
  {
    category: "race",
    note: "minorities",
    phrases: [
      "ethnic minorities",
      "ethnic minority",
      "minorities",
      "minority communities",
      "minority families",
      "minority group",
      "minority groups",
      "minority people",
      "minority populations",
      "minority students",
      "minority youth",
      "racial minorities",
      "racial minority",
    ],
    alternatives: [
      "name the specific groups",
      "people of color",
      "historically marginalized groups",
      "minoritized groups",
    ],
    notAfter: [
      "gender",
      "language",
      "linguistic",
      "national",
      "religious",
      "sexual",
    ],
  },
  {
    category: "race",
    note: "nonWhite",
    phrases: ["non-White", "non-Whites", "nonwhite", "nonwhites"],
    alternatives: ["name the specific groups", "people of color"],
  },
  {
    category: "race",
    note: "thirdWorld",
    phrases: ["Third World", "third-world"],
    alternatives: [
      "low- and middle-income countries",
      "the Global South",
      "name the countries",
    ],
    beforeName: true,
  },

  // ---- Sexual orientation and gender identity
  {
    category: "identity",
    note: "homosexual",
    phrases: ["homosexual", "homosexuals"],
    alternatives: ["gay", "lesbian", "gay men and lesbians", "LGBTQ+ people"],
  },
  {
    category: "identity",
    note: "sexualPreference",
    phrases: ["sexual preference", "sexual preferences"],
    alternatives: ["sexual orientation"],
  },
  {
    category: "identity",
    note: "transgender",
    phrases: ["transgendered", "transgenders"],
    alternatives: ["transgender", "transgender people"],
  },
  {
    category: "identity",
    note: "transgender",
    phrases: ["a transgender"],
    alternatives: ["a transgender person"],
    label: true,
  },

  // ---- Socioeconomic status
  {
    category: "ses",
    note: "adjectiveLabel",
    phrases: ["the poor"],
    alternatives: [
      "people with low incomes",
      "people whose income is below the poverty line",
    ],
    label: true,
  },
  {
    category: "ses",
    note: "homeless",
    phrases: ["the homeless"],
    alternatives: ["people experiencing homelessness"],
    label: true,
  },
  {
    category: "ses",
    note: "homeless",
    phrases: [
      "homeless families",
      "homeless individuals",
      "homeless men",
      "homeless people",
      "homeless person",
      "homeless persons",
      "homeless women",
      "homeless youth",
    ],
    alternatives: [
      "people experiencing homelessness",
      "people without housing",
    ],
  },
  {
    category: "ses",
    note: "incarcerated",
    phrases: ["ex-convict", "ex-convicts", "prisoner", "prisoners"],
    alternatives: [
      "person who is incarcerated",
      "people who are incarcerated",
      "person who was incarcerated",
    ],
    notAfter: ["political"],
    notBefore: ["'s dilemma", "dilemma", "of conscience", "of war"],
  },
  {
    category: "ses",
    note: "enslaved",
    phrases: ["slave", "slaves"],
    alternatives: ["enslaved person", "enslaved people"],
    notAfter: ["master"],
    notBefore: [
      "code",
      "codes",
      "economy",
      "holder",
      "holders",
      "holding",
      "labor",
      "labour",
      "market",
      "markets",
      "narrative",
      "narratives",
      "owner",
      "owners",
      "patrol",
      "patrols",
      "quarters",
      "rebellion",
      "rebellions",
      "revolt",
      "revolts",
      "ship",
      "ships",
      "societies",
      "society",
      "state",
      "states",
      "system",
      "systems",
      "trade",
      "trader",
      "traders",
    ],
  },
  {
    category: "ses",
    note: "sexWork",
    phrases: ["prostitute", "prostitutes"],
    alternatives: ["sex worker", "person who engages in sex work"],
  },
];

/** Words after "the poor", "the blind" that show it is used as a noun for people. */
const LABEL_FOLLOWERS = new Set([
  "across",
  "also",
  "among",
  "and",
  "are",
  "as",
  "at",
  "but",
  "by",
  "can",
  "could",
  "deserve",
  "did",
  "do",
  "does",
  "experience",
  "face",
  "for",
  "from",
  "had",
  "has",
  "have",
  "in",
  "into",
  "is",
  "lack",
  "live",
  "may",
  "might",
  "must",
  "need",
  "of",
  "often",
  "on",
  "or",
  "receive",
  "remain",
  "should",
  "tend",
  "than",
  "themselves",
  "through",
  "to",
  "was",
  "were",
  "who",
  "whose",
  "will",
  "with",
  "within",
  "would",
]);

/** Conditions that make "suffers from" about health. */
const CONDITION_WORDS = new Set([
  "addiction",
  "adhd",
  "aids",
  "alcoholism",
  "alzheimer's",
  "alzheimer’s",
  "anorexia",
  "anxiety",
  "arthritis",
  "asthma",
  "autism",
  "bipolar",
  "blindness",
  "bulimia",
  "cancer",
  "chronic",
  "deafness",
  "dementia",
  "depression",
  "diabetes",
  "disabilities",
  "disability",
  "disease",
  "diseases",
  "disorder",
  "disorders",
  "dyslexia",
  "epilepsy",
  "hiv",
  "illness",
  "illnesses",
  "infection",
  "injuries",
  "injury",
  "insomnia",
  "mental",
  "migraine",
  "migraines",
  "obesity",
  "ocd",
  "pain",
  "palsy",
  "paralysis",
  "parkinson's",
  "parkinson’s",
  "psychosis",
  "ptsd",
  "schizophrenia",
  "sclerosis",
  "stroke",
  "syndrome",
  "trauma",
]);

/** People as subjects: "patients who suffer from", "she suffers from". */
const PERSON_WORDS = new Set([
  "adolescents",
  "adults",
  "children",
  "clients",
  "he",
  "individuals",
  "many",
  "men",
  "millions",
  "participants",
  "patients",
  "people",
  "person",
  "persons",
  "she",
  "someone",
  "students",
  "they",
  "those",
  "veterans",
  "who",
  "women",
]);

export interface InclusiveMatch {
  start: number;
  end: number;
  text: string;
  category: InclusiveCategory;
  note: InclusiveNote;
  alternatives: readonly string[];
}

export interface InclusiveReport {
  matches: InclusiveMatch[];
  byCategory: Tally[];
  /** Matches skipped because they sit inside quotation marks. */
  quotedSkipped: number;
}

const COMPILED = RULES.map((rule) => ({
  rule,
  regex: phraseRegex(rule.phrases),
}));

const RE_WORD = /[\p{L}][\p{L}'’-]*/gu;
const RE_POSSESSIVE_START = /^['’]s\b/;
const RE_UPPER = /\p{Lu}/u;
const RE_STARTS_WORD = /^\s*\p{L}/u;

function wordsAfter(text: string, end: number, count: number): string[] {
  const rest = text.slice(end, end + 60);
  const possessive = RE_POSSESSIVE_START.exec(rest);
  const words = [...rest.slice(possessive ? 2 : 0).matchAll(RE_WORD)].map(
    (m) => m[0]
  );
  return [...(possessive ? ["'s"] : []), ...words].slice(0, count);
}

function wordsBefore(text: string, start: number, count: number): string[] {
  const before = text.slice(Math.max(0, start - 60), start);
  return [...before.matchAll(RE_WORD)].map((m) => m[0]).slice(-count);
}

function startsWithPhrase(words: readonly string[], phrase: string): boolean {
  const parts = phrase.split(" ");
  return parts.every(
    (part, index) => (words[index] ?? "").toLowerCase() === part
  );
}

function blockedByNeighbours(
  text: string,
  rule: InclusiveRule,
  start: number,
  end: number
): boolean {
  const after = wordsAfter(text, end, 3);
  if (rule.notBefore?.some((phrase) => startsWithPhrase(after, phrase))) {
    return true;
  }
  const previous = wordsBefore(text, start, 1)[0]?.toLowerCase() ?? "";
  return rule.notAfter?.includes(previous) ?? false;
}

/** "the poor are" and "the poor." are labels; "the poor results" is an adjective. */
function isLabelUse(text: string, end: number): boolean {
  if (!RE_STARTS_WORD.test(text.slice(end, end + 4))) {
    return true;
  }
  const next = (wordsAfter(text, end, 1)[0] ?? "").toLowerCase();
  return LABEL_FOLLOWERS.has(next);
}

function nameFollows(text: string, end: number): boolean {
  const next = wordsAfter(text, end, 1)[0] ?? "";
  return RE_UPPER.test(next.charAt(0));
}

function isTitleUse(text: string, start: number, end: number): boolean {
  return isCapitalizedMidSentence(text, start) || nameFollows(text, end);
}

function isAboutHealth(text: string, start: number, end: number): boolean {
  const after = wordsAfter(text, end, 6).map((word) => word.toLowerCase());
  const before = wordsBefore(text, start, 3).map((word) => word.toLowerCase());
  return (
    after.some((word) => CONDITION_WORDS.has(word)) ||
    before.some((word) => PERSON_WORDS.has(word))
  );
}

function properCapital(text: string, start: number, end: number): boolean {
  const words = text.slice(start, end).split(" ").slice(1);
  return words.some((word) => RE_UPPER.test(word.charAt(0)));
}

function applies(
  text: string,
  rule: InclusiveRule,
  start: number,
  end: number
): boolean {
  if (blockedByNeighbours(text, rule, start, end)) {
    return false;
  }
  if (rule.title && isTitleUse(text, start, end)) {
    return false;
  }
  if (rule.beforeName && nameFollows(text, end)) {
    return false;
  }
  if (rule.capitalizedIsProper && properCapital(text, start, end)) {
    return false;
  }
  if (rule.label && !isLabelUse(text, end)) {
    return false;
  }
  return !rule.health || isAboutHealth(text, start, end);
}

export function findInclusiveLanguageIssues(text: string): InclusiveReport {
  const quoted = quotedSpans(text);
  const found: InclusiveMatch[] = [];
  let quotedSkipped = 0;
  for (const { rule, regex } of COMPILED) {
    for (const hit of text.matchAll(regex)) {
      const start = hit.index;
      const end = start + hit[0].length;
      if (!applies(text, rule, start, end)) {
        continue;
      }
      if (insideAny(quoted, start, end)) {
        quotedSkipped += 1;
        continue;
      }
      found.push({
        start,
        end,
        text: hit[0],
        category: rule.category,
        note: rule.note,
        alternatives: rule.alternatives,
      });
    }
  }
  const matches = withoutOverlaps(found);
  return {
    matches,
    byCategory: tally(matches.map((match) => match.category)),
    quotedSkipped,
  };
}
